import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Users, 
  Calendar, CheckCircle, XCircle, Clock, Filter, Search
} from 'lucide-react';

const GerenciarInscricoes = () => {
  const navigate = useNavigate();
  const [inscricoes, setInscricoes] = useState([]);
  const [filtro, setFiltro] = useState('todos');
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    carregarInscricoes();
  }, []);

  const carregarInscricoes = () => {
    setLoading(true);
    try {
      const inscricoesStorage = JSON.parse(localStorage.getItem('inscricoes_dashboard') || '[]');
      setInscricoes(inscricoesStorage);
    } catch (error) {
      console.error('Erro ao carregar inscrições:', error);
    } finally {
      setLoading(false);
    }
  };

  const aprovarInscricao = (id) => {
    const inscricoesAtualizadas = inscricoes.map(inscricao => {
      if (inscricao.id === id) {
        return {
          ...inscricao,
          status: 'aprovada',
          dataAprovacao: new Date().toISOString(),
          credenciaisGeradas: true,
          emailGerado: `${inscricao.nome.toLowerCase().replace(/\s+/g, '.')}.${inscricao.cargo.toLowerCase().replace(/\s+/g, '')}@sitemov.com`,
          senhaGerada: `${inscricao.nome.split(' ')[0].toLowerCase()}2025`
        };
      }
      return inscricao;
    });
    
    setInscricoes(inscricoesAtualizadas);
    localStorage.setItem('inscricoes_dashboard', JSON.stringify(inscricoesAtualizadas));
  };

  const rejeitarInscricao = (id) => {
    const inscricoesAtualizadas = inscricoes.map(inscricao => {
      if (inscricao.id === id) {
        return {
          ...inscricao,
          status: 'rejeitada',
          dataRejeicao: new Date().toISOString()
        };
      }
      return inscricao;
    });
    
    setInscricoes(inscricoesAtualizadas);
    localStorage.setItem('inscricoes_dashboard', JSON.stringify(inscricoesAtualizadas));
  };

  const getStatusBadge = (status) => {
    const badges = {
      pendente: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pendente' },
      aprovada: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Aprovada' },
      rejeitada: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Rejeitada' }
    };
    
    const badge = badges[status] || badges.pendente;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {badge.text}
      </span>
    );
  };

  const inscricoesFiltradas = inscricoes.filter(inscricao => {
    const matchFiltro = filtro === 'todos' || inscricao.status === filtro;
    const matchBusca = !busca || 
      (inscricao.nome && String(inscricao.nome).toLowerCase().includes(busca.toLowerCase())) ||
      (inscricao.email && String(inscricao.email).toLowerCase().includes(busca.toLowerCase())) ||
      (inscricao.empresa && String(inscricao.empresa).toLowerCase().includes(busca.toLowerCase()));
    
    return matchFiltro && matchBusca;
  });

  const estatisticas = {
    total: inscricoes.length,
    pendentes: inscricoes.filter(i => i.status === 'pendente').length,
    aprovadas: inscricoes.filter(i => i.status === 'aprovada').length,
    rejeitadas: inscricoes.filter(i => i.status === 'rejeitada').length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm font-medium">Voltar</span>
            </button>
            <div className="h-8 w-px bg-gray-300"></div>
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
                <Users className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Gerenciar Inscrições
                </h1>
                <p className="text-sm text-gray-600">
                  Sistema de aprovação de acesso ao dashboard
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-3xl font-bold text-gray-900">{estatisticas.total}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pendentes</p>
                <p className="text-3xl font-bold text-yellow-600">{estatisticas.pendentes}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Aprovadas</p>
                <p className="text-3xl font-bold text-green-600">{estatisticas.aprovadas}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Rejeitadas</p>
                <p className="text-3xl font-bold text-red-600">{estatisticas.rejeitadas}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="todos">Todos os Status</option>
                <option value="pendente">Pendentes</option>
                <option value="aprovada">Aprovadas</option>
                <option value="rejeitada">Rejeitadas</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Search className="h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, e-mail ou empresa..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-80"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Carregando dados...</p>
            </div>
          ) : inscricoesFiltradas.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Nenhuma inscrição encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Candidato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cargo/Empresa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inscricoesFiltradas.map((inscricao) => (
                    <tr key={inscricao.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600">
                                {inscricao.nome.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {inscricao.nome}
                            </div>
                            <div className="text-sm text-gray-500">
                              {inscricao.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{inscricao.cargo}</div>
                        <div className="text-sm text-gray-500">{inscricao.empresa}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(inscricao.dataInscricao).toLocaleDateString('pt-BR')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(inscricao.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {inscricao.status === 'pendente' && (
                            <>
                              <button
                                onClick={() => aprovarInscricao(inscricao.id)}
                                className="text-green-600 hover:text-green-900 p-1 rounded"
                                title="Aprovar"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => rejeitarInscricao(inscricao.id)}
                                className="text-red-600 hover:text-red-900 p-1 rounded"
                                title="Rejeitar"
                              >
                                <XCircle className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {inscricao.status === 'aprovada' && inscricao.credenciaisGeradas && (
                            <div className="text-xs bg-green-50 p-2 rounded border">
                              <p><strong>Email:</strong> {inscricao.emailGerado}</p>
                              <p><strong>Senha:</strong> {inscricao.senhaGerada}</p>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GerenciarInscricoes; 
